import { HierarchicalFacetNode, FacetGroupType, FacetValue } from '../types';

export interface FacetTreeBuilderOptions {
  selectedPaths: string[][];
  expandedPaths: string[][];
}

export function buildHierarchicalTree(
  facetType: FacetGroupType,
  options: FacetTreeBuilderOptions
): HierarchicalFacetNode[] {
  if (facetType.type !== 'categorical' || !facetType.values) {
    return [];
  }

  const { selectedPaths, expandedPaths } = options;
  const nodeMap = new Map<string, HierarchicalFacetNode>();
  const rootNodes: HierarchicalFacetNode[] = [];

  // First pass: create all nodes from facet values
  facetType.values.forEach((facetValue) => {
    const { path, count, has_children } = facetValue;
    const pathKey = path.join('/');
    const level = path.length - 1;
    const displayValue = path[path.length - 1] || 'All';

    const isSelected = selectedPaths.some(
      (selectedPath) =>
        selectedPath.length === path.length &&
        selectedPath.every((segment, index) => segment === path[index])
    );

    const isExpanded = expandedPaths.some(
      (expandedPath) =>
        expandedPath.length === path.length &&
        expandedPath.every((segment, index) => segment === path[index])
    );

    const node: HierarchicalFacetNode = {
      path: [...path],
      displayValue,
      count,
      level,
      has_children,
      isExpanded,
      isSelected,
      children: [],
    };

    nodeMap.set(pathKey, node);
  });

  // Add selected paths that aren't in the results (with 0 count)
  selectedPaths.forEach((selectedPath) => {
    const pathKey = selectedPath.join('/');
    if (!nodeMap.has(pathKey)) {
      const level = selectedPath.length - 1;
      const displayValue = selectedPath[selectedPath.length - 1] || 'All';

      const isExpanded = expandedPaths.some(
        (expandedPath) =>
          expandedPath.length === selectedPath.length &&
          expandedPath.every(
            (segment, index) => segment === selectedPath[index]
          )
      );

      const node: HierarchicalFacetNode = {
        path: [...selectedPath],
        displayValue,
        count: 0,
        level,
        has_children: false,
        isExpanded,
        isSelected: true,
        children: [],
      };

      nodeMap.set(pathKey, node);
    }
  });

  // Second pass: build parent-child relationships
  nodeMap.forEach((node) => {
    if (node.path.length === 1) {
      // Root level node
      rootNodes.push(node);
    } else {
      // Child node - find parent
      const parentPath = node.path.slice(0, -1);
      const parentKey = parentPath.join('/');
      const parent = nodeMap.get(parentKey);

      if (parent) {
        parent.children = parent.children || [];
        parent.children.push(node);
      }
    }
  });

  // Sort nodes at each level: selected first, then by count descending
  const sortNodes = (nodes: HierarchicalFacetNode[]) => {
    nodes.sort((a, b) => {
      // Selected facets first
      if (a.isSelected && !b.isSelected) return -1;
      if (!a.isSelected && b.isSelected) return 1;
      // Then by count descending
      return b.count - a.count;
    });

    // Recursively sort children
    nodes.forEach((node) => {
      if (node.children && node.children.length > 0) {
        sortNodes(node.children);
      }
    });
  };

  sortNodes(rootNodes);
  return rootNodes;
}

// Helper function to extract all expanded paths from a tree structure
export function extractExpandedPaths(
  nodes: HierarchicalFacetNode[]
): string[][] {
  const expandedPaths: string[][] = [];

  const traverse = (nodeList: HierarchicalFacetNode[]) => {
    nodeList.forEach((node) => {
      if (node.isExpanded && node.children && node.children.length > 0) {
        expandedPaths.push([...node.path]);
        traverse(node.children);
      }
    });
  };

  traverse(nodes);
  return expandedPaths;
}

// Helper function to convert old flat facet structure to hierarchical
export function convertLegacyFacetValues(
  values: [string, number][]
): FacetValue[] {
  return values.map(([value, count]) => {
    // If it's already an array, keep it; if it's a string, wrap it
    const path = Array.isArray(value) ? value : [value];
    return {
      path,
      count,
      has_children: false, // Legacy values don't have this info, so default to false
    };
  });
}
